// ./src/utils/api/apiResultHandler.ts
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";

// ============================================
// Types
// ============================================

/**
 * Categorized error types for easier handling in UI
 */
export type ApiErrorCategory =
  | "auth" // UNAUTHORIZED, FORBIDDEN, EXPIRED, token errors
  | "not_found" // NOT_FOUND
  | "validation" // VALIDATION_FAILED
  | "conflict" // CONFLICT
  | "limit" // LIMIT_REACHED
  | "server" // FAILED, EXCEPTION
  | "network" // NETWORK_ERROR, TIMEOUT
  | "unknown";

// ============================================
// ApiError Class
// ============================================

/**
 * Structured error that wraps OpResult with convenience methods.
 * Use this instead of storing raw error strings or OpResult directly.
 */
export class ApiError {
  /** Original OpResult for full access to errors array, code, etc. */
  readonly result: OpResult;

  /** Categorized error type for conditional UI handling */
  readonly category: ApiErrorCategory;

  /** Human-readable error message */
  readonly message: string;

  constructor(result: OpResult, fallbackMessage = "An unexpected error occurred", errorField = "") {
    this.result = result;
    this.category = ApiError.getCategory(result.code);

    const errorSummary = result.getErrorSummary(errorField);
    this.message = errorSummary || fallbackMessage;
  }

  // ============================================
  // Convenience checkers
  // ============================================

  /** Check if error requires re-authentication */
  get isAuth(): boolean {
    return this.category === "auth";
  }

  /** Check if error is a server-side issue (user should retry later) */
  get isServer(): boolean {
    return this.category === "server";
  }

  /** Check if error is network-related (user should check connection) */
  get isNetwork(): boolean {
    return this.category === "network";
  }

  /** Check if error indicates resource not found */
  get isNotFound(): boolean {
    return this.category === "not_found";
  }

  /** Check if error is validation-related */
  get isValidation(): boolean {
    return this.category === "validation";
  }

  /** Check if error is a conflict (e.g., duplicate) */
  get isConflict(): boolean {
    return this.category === "conflict";
  }

  /** Check if error indicates limit reached */
  get isLimitReached(): boolean {
    return this.category === "limit";
  }

  /** Check if error is retryable (network or server errors) */
  get isRetryable(): boolean {
    return this.category === "network" || this.category === "server";
  }

  /** Get the OpResult code */
  get code(): number {
    return this.result.code;
  }

  /** Check if specific OpResult code */
  hasCode(code: number): boolean {
    return this.result.code === code;
  }

  /** Get field-specific errors from the underlying OpResult */
  getFieldErrors(field: string): string[] {
    return this.result.getFieldErrors(field);
  }

  // ============================================
  // Static factory methods
  // ============================================

  /**
   * Determines the error category from an OpResult code
   */
  static getCategory(code: number): ApiErrorCategory {
    switch (code) {
      case OP_RESULT_CODES.UNAUTHORIZED:
      //case OP_RESULT_CODES.FORBIDDEN:
      case OP_RESULT_CODES.EXPIRED:
      case OP_RESULT_CODES.INVALID_SIGNATURE:
      case OP_RESULT_CODES.INVALID_AUDIENCE:
      case OP_RESULT_CODES.INVALID_ISSUER:
      case OP_RESULT_CODES.MALFORMED_TOKEN:
      case OP_RESULT_CODES.NO_SIGNATURE:
        return "auth";

      case OP_RESULT_CODES.NOT_FOUND:
        return "not_found";

      case OP_RESULT_CODES.VALIDATION_FAILED:
        return "validation";

      case OP_RESULT_CODES.CONFLICT:
        return "conflict";

      case OP_RESULT_CODES.LIMIT_REACHED:
        return "limit";

      case OP_RESULT_CODES.FAILED:
      case OP_RESULT_CODES.EXCEPTION:
        return "server";

      case OP_RESULT_CODES.NETWORK_ERROR:
      case OP_RESULT_CODES.TIMEOUT:
        return "network";

      default:
        return "unknown";
    }
  }

  /**
   * Creates an ApiError from an OpResult
   */
  static fromResult(
    result: OpResult,
    fallbackMessage = "An unexpected error occurred",
    errorField = ""
  ): ApiError {
    return new ApiError(result, fallbackMessage, errorField);
  }

  /**
   * Creates an ApiError from a caught exception (network errors, etc.)
   */
  static fromException(
    error: unknown,
    fallbackMessage = "A network error occurred"
  ): ApiError {
    const result = OpResult.fromException(error);
    // Override the code to NETWORK_ERROR for caught exceptions
    result.setCode(OP_RESULT_CODES.NETWORK_ERROR);

    const message = error instanceof Error ? error.message : fallbackMessage;

    return new ApiError(result, message);
  }

  /**
   * Extract field-specific error message from ApiError.
   * Returns the first error message for the given field, or undefined if none.
   * Useful for displaying validation errors next to form inputs.
   *
   * @param error - ApiError instance (or null/undefined)
   * @param fieldName - The field name to get errors for
   * @returns First error message for the field, or undefined
   *
   * @example
   * const emailError = ApiError.getFieldError(lastError, "email");
   * <TextInput error={emailError} />
   */
  static getFieldError(
    error: ApiError | null | undefined,
    fieldName: string
  ): string | undefined {
    if (!error?.result?.errors) return undefined;

    const fieldErrors = error.getFieldErrors(fieldName);
    return fieldErrors.length > 0 ? fieldErrors[0] : undefined;
  }

  /**
   * Extract generic (non-field-specific) error message from ApiError.
   * These are errors with name === '' in the errors array.
   * Useful for displaying at the top of forms or in alert banners.
   *
   * @param error - ApiError instance (or null/undefined)
   * @returns First generic error message, or undefined
   *
   * @example
   * const genericError = ApiError.getGenericError(lastError);
   * {genericError && <Alert color="red">{genericError}</Alert>}
   */
  static getGenericError(
    error: ApiError | null | undefined
  ): string | undefined {
    if (!error?.result?.errors) return undefined;

    const genericErrors = error.getFieldErrors("");
    return genericErrors.length > 0 ? genericErrors[0] : undefined;
  }

  /**
   * Extract all form errors, mapping known fields to their errors and
   * collecting unknown field errors as generic errors.
   * 
   * This is useful for forms where:
   * - Known fields should show errors next to their inputs
   * - Unknown fields (or fields not in the form) should be shown as generic errors
   * - Generic errors (name === '') should be collected and shown near the submit button
   *
   * @param error - ApiError instance (or null/undefined)
   * @param knownFields - Array of field names that exist in the form
   * @returns Object with field errors and combined generic errors array
   *
   * @example
   * const { fields, genericErrors } = ApiError.getFormErrors(lastError, ["email", "password"]);
   * <TextInput error={fields.email} />
   * <PasswordInput error={fields.password} />
   * {genericErrors.length > 0 && <Alert color="red">{genericErrors.join(". ")}</Alert>}
   */
  static getFormErrors<T extends string>(
    error: ApiError | null | undefined,
    knownFields: T[]
  ): {
    fields: Record<T, string | undefined>;
    genericErrors: string[];
  } {
    // Initialize fields object with undefined for all known fields
    const fields = {} as Record<T, string | undefined>;
    for (const field of knownFields) {
      fields[field] = undefined;
    }

    const genericErrors: string[] = [];

    if (!error?.result?.errors) {
      return { fields, genericErrors };
    }

    const knownFieldSet = new Set<string>(knownFields);

    for (const errorItem of error.result.errors) {
      const fieldName = errorItem.name || "";
      const errorMessages = errorItem.errors || [];

      if (errorMessages.length === 0) continue;

      if (fieldName === "") {
        // Generic error - add all messages
        genericErrors.push(...errorMessages);
      } else if (knownFieldSet.has(fieldName)) {
        // Known field - assign first error to field
        fields[fieldName as T] = errorMessages[0];
      } else {
        // Unknown field - treat as generic error
        // Prefix with field name for context
        const prefixedErrors = errorMessages.map(
          (msg) => `${fieldName}: ${msg}`
        );
        genericErrors.push(...prefixedErrors);
      }
    }

    return { fields, genericErrors };
  }

  /**
   * Format an array of error messages for display.
   * Joins multiple errors with periods and proper spacing.
   *
   * @param errors - Array of error messages
   * @returns Formatted error string, or undefined if no errors
   *
   * @example
   * const formatted = ApiError.formatErrors(genericErrors);
   * // "First error. Second error. Third error"
   */
  static formatErrors(errors: string[]): string | undefined {
    if (!errors || errors.length === 0) return undefined;
    return errors.join(". ");
  }
}